// Centralized fetch helper + speech utility used by all Academy components.
const API = `${process.env.REACT_APP_BACKEND_URL}/api/academy`;

export const apiCall = async (path, method = 'GET', body = null, token = null) => {
  const opts = { method, headers: { 'Content-Type': 'application/json' } };
  if (token) opts.headers['Authorization'] = `Bearer ${token}`;
  if (body) opts.body = JSON.stringify(body);
  const r = await fetch(`${API}${path}`, opts);
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.detail || 'Erreur');
  }
  return r.json();
};

let _audioEl = null;

// If the text is a sequence of single letters (e.g. "A B C D E F G"), insert commas
// so the voice reads it letter by letter instead of as one word.
const _spellIfLetters = (text) => {
  const tokens = (text || '').trim().split(/\s+/);
  if (tokens.length < 3) return text;
  const singles = tokens.filter(t => t.replace(/[.,;:]/g, '').length === 1).length;
  if (singles / tokens.length > 0.7) {
    return tokens.map(t => t.replace(/[.,;:]+$/, '')).join(', ');
  }
  return text;
};

const _browserSpeak = (text, lang = 'en-US', rate = 0.9) => new Promise((resolve) => {
  if (!('speechSynthesis' in window)) return resolve();
  window.speechSynthesis.cancel();
  const utt = new SpeechSynthesisUtterance(_spellIfLetters(text));
  utt.lang = lang;
  utt.rate = rate;
  utt.onend = resolve;
  utt.onerror = resolve;
  window.speechSynthesis.speak(utt);
});

// Natural neural voice (OpenAI TTS) with graceful fallback to the browser voice.
export const speak = (text, lang = 'en-US', rate = 0.9, voice) => new Promise(async (resolve) => {
  stopSpeaking();
  const spoken = _spellIfLetters(text);
  const v = voice || 'nova';
  try {
    const r = await fetch(`${API}/tts`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text: spoken, voice: v }),
    });
    if (!r.ok) throw new Error('tts-unavailable');
    const { url } = await r.json();
    const audio = new Audio(`${process.env.REACT_APP_BACKEND_URL}${url}`);
    _audioEl = audio;
    audio.playbackRate = rate <= 0.75 ? 0.85 : (rate >= 1.0 ? 1.1 : 1.0);
    let settled = false;
    const done = () => { if (!settled) { settled = true; _audioEl = null; resolve(); } };
    audio.onended = done;
    audio.onerror = () => { if (!settled) { settled = true; _audioEl = null; _browserSpeak(text, lang, rate).then(resolve); } };
    await audio.play().catch(() => { if (!settled) { settled = true; _audioEl = null; _browserSpeak(text, lang, rate).then(resolve); } });
  } catch (e) {
    _browserSpeak(text, lang, rate).then(resolve);
  }
});

export const stopSpeaking = () => {
  if (_audioEl) { try { _audioEl.pause(); } catch (e) {} _audioEl = null; }
  if ('speechSynthesis' in window) window.speechSynthesis.cancel();
};

export const ENCOURAGEMENTS = [
  "Bien joué !", "Continue, tu progresses !", "Excellent !", "Tu es sur la bonne voie.",
  "Bravo, encore une victoire !", "Tu deviens meilleur chaque jour.", "Génial !",
  "Tu y es presque !", "Magnifique !", "Tu maîtrises !"
];
export const randomEncouragement = () => ENCOURAGEMENTS[Math.floor(Math.random() * ENCOURAGEMENTS.length)];

// Shuffle QCM options so the correct answer isn't always in the same position.
// Remaps a numeric `answer` index to the new position. String answers are left as-is.
export const shuffleQuiz = (questions) => (questions || []).map((q) => {
  if (!Array.isArray(q.options)) return q;
  const idx = q.options.map((_, i) => i);
  for (let i = idx.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [idx[i], idx[j]] = [idx[j], idx[i]];
  }
  const options = idx.map((i) => q.options[i]);
  const answer = typeof q.answer === 'number' ? idx.indexOf(q.answer) : q.answer;
  return { ...q, options, answer };
});

export const shuffleArray = (arr) => {
  const a = [...(arr || [])];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

// ===== Analytics légères : visites + clics sur les modules (visiteurs avec ou sans compte) =====
const _visitorId = () => {
  let id = localStorage.getItem('academyVisitorId');
  if (!id) {
    id = 'v_' + Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem('academyVisitorId', id);
  }
  return id;
};

// Envoi "fire-and-forget" : n'interrompt jamais l'UI même hors ligne.
export const track = (event, view = null, label = null, authed = false) => {
  try {
    fetch(`${API}/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      keepalive: true,
      body: JSON.stringify({ event, view, label, authed, visitor_id: _visitorId() }),
    }).catch(() => {});
  } catch (e) { /* ignore */ }
};
